import React, { useCallback, useEffect, useState } from 'react';
import {
  DECISIONS_NEEDING_A_NOTE,
  MEMBER_STATUSES,
  decideMember,
  listMembers,
  type MemberProfile,
} from '../../../services/memberAdminService';

/**
 * Reviewing community membership applications.
 *
 * A profile is created PENDING and is not listed publicly until somebody approves it.
 * Nothing could approve one from a screen, so the community directory stays empty no
 * matter how many people apply - the same shape as casting: the capability existed, and
 * nobody could reach it.
 *
 * Opens on the pending queue, because that is the only tab with anything waiting on a
 * person. The approved and removed tabs are for looking something up afterwards.
 */

const TABS = [
  { value: 'PENDING', label: 'Awaiting review' },
  { value: 'ACTIVE', label: 'Approved' },
  { value: 'HIDDEN', label: 'Hidden' },
  { value: 'ARCHIVED', label: 'Removed' },
];

const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:ring-2 focus:ring-orange-500';

const MemberManagement: React.FC = () => {
  const [tab, setTab] = useState('PENDING');
  const [members, setMembers] = useState<MemberProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async (status: string) => {
    setLoading(true);
    try {
      setMembers(await listMembers(status));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load members');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(tab); }, [tab, refresh]);

  return (
    <div className="p-6 bg-white min-h-screen">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900">Community members</h1>
        <p className="mb-6 text-sm text-gray-600">
          People apply to join. Nobody appears in the public directory until they are approved here.
        </p>

        <div role="tablist" className="mb-6 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.value}
              role="tab"
              aria-selected={tab === t.value}
              onClick={() => setTab(t.value)}
              className={`rounded-full px-4 py-2 text-sm font-medium ${
                tab === t.value ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {error && <p role="alert" className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
        {notice && <p className="mb-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">{notice}</p>}

        {loading ? (
          <p className="text-sm text-gray-600">Loading…</p>
        ) : members.length === 0 ? (
          <p className="rounded-xl border border-gray-200 px-4 py-6 text-sm text-gray-600">
            {tab === 'PENDING'
              ? 'Nobody is waiting for review. Applications appear here as they are submitted.'
              : 'Nobody in this list yet.'}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200">
            {members.map((member) => (
              <MemberRow
                key={member.id}
                member={member}
                onDecided={(updated, label) => {
                  setNotice(`${updated.displayName} — ${label}.`);
                  // Leaves the list it no longer belongs to, which is what makes the
                  // pending queue drain as it is worked through.
                  setMembers((prev) => prev.filter((m) => m.id !== updated.id || updated.status === tab));
                }}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const MemberRow: React.FC<{
  member: MemberProfile;
  onDecided: (updated: MemberProfile, label: string) => void;
}> = ({ member, onDecided }) => {
  const [status, setStatus] = useState(member.status === 'PENDING' ? 'ACTIVE' : member.status);
  const [note, setNote] = useState('');
  const [founding, setFounding] = useState(member.foundingMember);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const chosen = MEMBER_STATUSES.find((s) => s.value === status);
  const noteRequired = DECISIONS_NEEDING_A_NOTE.includes(status) && status !== member.status;

  const submit = async () => {
    if (noteRequired && !note.trim()) {
      // Caught here so the reviewer is told before losing what they typed. The server
      // enforces the same rule, and that is the check that actually holds.
      setProblem('A note is required for this decision. It is sent to the member.');
      return;
    }
    setSaving(true);
    setProblem(null);
    try {
      const updated = await decideMember(member.slug, status, note.trim(), founding);
      onDecided(updated, chosen?.label ?? status);
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'Could not save the decision');
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="px-4 py-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-gray-900">{member.displayName}</p>
        <p className="text-xs text-gray-600">
          {member.primaryCraftLabel ?? member.primaryCraft ?? 'No craft given'}
          {member.location ? ` · ${member.location}` : ''}
          {member.joinedAt ? ` · applied ${member.joinedAt.slice(0, 10)}` : ''}
        </p>
      </div>
      {member.headline && <p className="text-sm text-gray-800">{member.headline}</p>}
      {member.bio && <p className="mt-1 whitespace-pre-line text-sm text-gray-700">{member.bio}</p>}

      <div className="mt-2 flex flex-wrap gap-3 text-xs">
        {member.showreelUrl && <a className="text-orange-700 underline" href={member.showreelUrl} target="_blank" rel="noreferrer">Showreel</a>}
        {member.websiteUrl && <a className="text-orange-700 underline" href={member.websiteUrl} target="_blank" rel="noreferrer">Website</a>}
        {member.imdbUrl && <a className="text-orange-700 underline" href={member.imdbUrl} target="_blank" rel="noreferrer">IMDb</a>}
        {member.instagramUrl && <a className="text-orange-700 underline" href={member.instagramUrl} target="_blank" rel="noreferrer">Instagram</a>}
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-[12rem_1fr_auto] sm:items-start">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label={`Decision for ${member.displayName}`}
          className={inputClass}
        >
          {MEMBER_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <div>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            aria-label={`Note to ${member.displayName}`}
            placeholder={noteRequired ? 'Required — this is sent to the member' : 'Optional note'}
            className={inputClass}
          />
          <p className="mt-1 text-xs text-gray-600">{chosen?.help}</p>
        </div>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={saving}
          className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      <label className="mt-2 flex items-center gap-2 text-xs text-gray-700">
        <input type="checkbox" checked={founding} onChange={(e) => setFounding(e.target.checked)} />
        Founding member
      </label>

      {problem && <p role="alert" className="mt-2 text-sm text-red-700">{problem}</p>}
    </li>
  );
};

export default MemberManagement;
